import { useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { axiosInstance } from '@/modules/shared/lib/axios'

export type PanelStatus =
  | 'paid'
  | 'unpaid'
  | 'not_in_panel'
  | 'no_nip'
  | 'error'

export interface PaymentCheckCompany {
  companyId: string
  companyName: string
  nip: string | null
  nipSource: 'config' | 'operational' | 'none'
  configNip: string | null
  panelStatus: PanelStatus
  panelStatusCode: number | null
  panelError: string | null
  paid: boolean
  paymentDate: string | null
  subscriptionExpirationDate: string | null
  permanentlyBlocked: boolean
  // True when the company is grouped under a service account (konto usługowe) and is
  // therefore billed on that account's NIP (the `nip` above is the service account's).
  billedViaServiceAccount: boolean
  serviceAccountName: string | null
}

export const PAYMENT_CHECK_COMPANIES_QUERY_KEY = ['admin.paymentCheck.companies']

export function useQueryPaymentCheckCompanies() {
  // When the admin clicks "Odśwież" we want a real re-check against the panel, not the
  // server's 15-min cached verdict. A normal refetch (mount, window focus) must NOT force.
  const forceRef = useRef(false)

  const query = useQuery<PaymentCheckCompany[]>({
    queryKey: PAYMENT_CHECK_COMPANIES_QUERY_KEY,
    queryFn: async () => {
      const force = forceRef.current
      forceRef.current = false
      const { data } = await axiosInstance.get(
        `/config-api/admin/payment-check/companies${force ? '?force=true' : ''}`,
      )
      return data
    },
    staleTime: 60_000,
  })

  const refreshForced = () => {
    forceRef.current = true
    return query.refetch()
  }

  return { ...query, refreshForced }
}
